const REVEAL_SELECTOR = "[data-tw-image-reveal]";
const KEYBOARD_STEP = 5;
const LABEL_SWITCH_THRESHOLD = 55;

function clampPosition(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function applyRevealPosition(container: HTMLElement, position: number): void {
  const overlay = container.querySelector<HTMLElement>("[data-reveal-overlay]");
  const line = container.querySelector<HTMLElement>("[data-reveal-line]");
  const handle = container.querySelector<HTMLElement>("[data-reveal-handle]");
  const labelLeft = container.querySelector<HTMLElement>('[data-reveal-label="left"]');
  const labelRight = container.querySelector<HTMLElement>('[data-reveal-label="right"]');
  if (!overlay || !line || !handle) return;

  const pct = clampPosition(position);
  overlay.style.clipPath = `inset(0 calc(100% - ${pct}%) 0 0)`;
  line.style.left = `${pct}%`;
  handle.style.left = `${pct}%`;
  handle.setAttribute("aria-valuenow", String(Math.round(pct)));

  if (labelLeft && labelRight) {
    const showLeft = pct >= LABEL_SWITCH_THRESHOLD;
    labelLeft.classList.toggle("hidden", !showLeft);
    labelRight.classList.toggle("hidden", showLeft);
  }

  container.dataset.revealPct = String(pct);
}

function positionFromClientX(viewport: HTMLElement, clientX: number): number {
  const rect = viewport.getBoundingClientRect();
  if (rect.width <= 0) return 50;
  return clampPosition(((clientX - rect.left) / rect.width) * 100);
}

function initImageRevealContainer(container: HTMLElement): void {
  if (container.dataset.revealInit === "true") return;
  container.dataset.revealInit = "true";

  const viewport = container.querySelector<HTMLElement>("[data-reveal-viewport]");
  const handle = container.querySelector<HTMLButtonElement>("[data-reveal-handle]");
  if (!viewport || !handle) return;

  const initialPosition = Number(container.dataset.revealPct ?? 50);
  applyRevealPosition(container, Number.isFinite(initialPosition) ? initialPosition : 50);

  let activePointerId: number | null = null;

  const updateFromPointer = (clientX: number) => {
    applyRevealPosition(container, positionFromClientX(viewport, clientX));
  };

  const endDrag = (pointerId: number) => {
    if (activePointerId !== pointerId) return;
    activePointerId = null;
    if (handle.hasPointerCapture(pointerId)) {
      handle.releasePointerCapture(pointerId);
    }
    if (viewport.hasPointerCapture(pointerId)) {
      viewport.releasePointerCapture(pointerId);
    }
  };

  handle.addEventListener("pointerdown", (event) => {
    activePointerId = event.pointerId;
    handle.setPointerCapture(event.pointerId);
    updateFromPointer(event.clientX);
    event.preventDefault();
  });

  viewport.addEventListener("pointerdown", (event) => {
    if (event.target === handle || handle.contains(event.target as Node)) return;
    activePointerId = event.pointerId;
    viewport.setPointerCapture(event.pointerId);
    updateFromPointer(event.clientX);
    event.preventDefault();
  });

  for (const target of [handle, viewport]) {
    target.addEventListener("pointermove", (event) => {
      if (activePointerId !== event.pointerId) return;
      updateFromPointer(event.clientX);
    });

    target.addEventListener("pointerup", (event) => {
      endDrag(event.pointerId);
    });

    target.addEventListener("pointercancel", (event) => {
      endDrag(event.pointerId);
    });
  }

  handle.addEventListener("keydown", (event) => {
    const current = Number(container.dataset.revealPct ?? 50);
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      applyRevealPosition(container, current - KEYBOARD_STEP);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      applyRevealPosition(container, current + KEYBOARD_STEP);
    } else if (event.key === "Home") {
      event.preventDefault();
      applyRevealPosition(container, 0);
    } else if (event.key === "End") {
      event.preventDefault();
      applyRevealPosition(container, 100);
    }
  });
}

export function initTwImageReveal(root: ParentNode = document): void {
  for (const container of Array.from(root.querySelectorAll<HTMLElement>(REVEAL_SELECTOR))) {
    initImageRevealContainer(container);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initTwImageReveal());
} else {
  initTwImageReveal();
}

document.addEventListener("shopify:section:load", (event) => {
  const target = (event.target as ParentNode) || document;
  initTwImageReveal(target);
});

document.addEventListener("shopify:block:select", (event) => {
  const target = (event.target as ParentNode) || document;
  initTwImageReveal(target);
});
